// @flow
import React from 'react';
import { intlShape } from 'react-intl';
import classNames from 'classnames';
import queryString from 'query-string';
import HeaderLayout from './core/header-layout/HeaderLayoutComponent';
import SideNavComponent from './core/side-nav/SideNavComponent';
import SideNavItem from './core/side-nav/item/SideNavItemComponent';
import Overlay from './common/overlay/OverlayComponent';
import Footer from './core/footer/FooterComponent';
import Toast from './common/toast/ToastComponent';
import { getProductSizeIntlSingularKey } from './services/product-type/ProductTypeService';
import { getMessageText } from './services/message/MessageService';
import { getNavItems, headerLinks } from './data/NavigationItems';
import toastMsg from '../data/toast';
import sidenavMessages from '../data/sidenav';
import accessoriesMessages from '../data/accessories';
import AnalyticsClient from '../clients/AnalyticsClient';
import toastEvents from '../analytics/events/toasts';
import productEvents from '../analytics/events/products';
import approvalEvents from '../analytics/events/approvals';

import './MainComponent.scss';

/**
 * Entry point component for the App (wrapped in Provider #Redux)
 */
class AppComponent extends React.Component {
  componentDidMount(): void {
    this.history();
    this.buildPixelToast();
  }

  history(): void {
    this.props.history.listen(() => {
      this.props.coreActions.closeHamburgerNav();
    });
  }

  getProductSizeName(productSize: string) {
    const message = getProductSizeIntlSingularKey(productSize) || {};
    const fallbackMessage = getProductSizeIntlSingularKey('businesscard');

    return getMessageText(this.context.intl, {
      message,
      fallbackMessage,
    });
  }

  showPixelToast(message: string, productSize: string): void {
    this.props.coreActions.showToast({
      message: {
        messageDescriptor: message,
        values: { productType: this.getProductSizeName(productSize) },
      },
    });
  }

  buildPixelToast(): void {
    const { pixelCreate, pixelEdit, isProductSubmittedForApproval, isProductEditedAndApproved } = queryString.parse(
      this.props.location.search,
    );

    const isCreate = !!pixelCreate;
    const isEdit = !!pixelEdit;
    const productSize = pixelCreate || pixelEdit;

    if (isProductSubmittedForApproval) {
      AnalyticsClient.trackEvent(productEvents.productSubmittedForApproval(productSize));
      this.props.coreActions.showToast({
        message: {
          messageDescriptor: toastMsg.approvalSentSuccessMessage,
        },
      });
      return;
    }

    if (isProductEditedAndApproved) {
      AnalyticsClient.trackEvent(approvalEvents.productEditedAndApproved());
      this.props.coreActions.showToast({
        message: {
          messageDescriptor: toastMsg.productEditedAndApprovedSuccessMessage,
        },
      });
      return;
    }

    if (isCreate) {
      AnalyticsClient.trackEvent(productEvents.newProductCreated(productSize));
      this.showPixelToast(toastMsg.pixelSuccessCreate, productSize);
    } else if (isEdit) {
      AnalyticsClient.trackEvent(productEvents.productEdited(productSize));
      this.showPixelToast(toastMsg.pixelSuccessUpdate, productSize);
    }
  }

  displayCurrentOverlay(): Element {
    const { overlayName = '', isActive = false } = this.props.overlay;
    const { overlayContent = '' } = this.props.overlay;

    return (
      <Overlay isActive={isActive} overlayName={overlayName} actions={this.props.coreActions}>
        {overlayContent}
      </Overlay>
    );
  }

  activeLink(item: { url: string, name?: string }): boolean {
    if (item.name === 'home') {
      return window.location.pathname === item.url;
    }

    return window.location.pathname.startsWith(item.url);
  }

  getBadgeValue(item: { url: string, name?: string }): ?number {
    if (item.name === 'approvals' && this.props.approvals.pendingCount) {
      return this.props.approvals.pendingCount;
    }
    return null;
  }

  getSideNavAnnouncement(itemName: string): ?{ title: string, body: string } {
    const { formatMessage } = this.context.intl;
    if (this.props.sideNavigation.displaySideNavAnnouncement && itemName === 'accessories') {
      return {
        title: formatMessage(accessoriesMessages.accessoriesAnnouncementTitle),
        body: formatMessage(accessoriesMessages.accessoriesAnnouncementBody),
        callout: formatMessage(accessoriesMessages.accessoriesAnnouncementCallout),
        onDismiss: this.props.coreActions.dismissSideNavAnnouncement,
      };
    }
    return null;
  }

  getSideNavItems(): Array<Element> {
    const getItemTitle = (itemName) => this.context.intl.formatMessage(sidenavMessages[`sideNavLink_${itemName}`]);
    const items = getNavItems(this.props.config.permissions, this.props.config.editableAccountSettings);

    return items.map((item) => (
      <SideNavItem
        key={item.name}
        name={item.name}
        title={getItemTitle(item.name)}
        icon={item.icon}
        link={item.link}
        url={item.url}
        active={this.activeLink(item)}
        onClick={() => {
          this.props.coreActions.navigateTo(`/${item.name}`);
        }}
        badgeValue={this.getBadgeValue(item)}
        announcementMsg={this.getSideNavAnnouncement(item.name)}
      />
    ));
  }

  showNotificationElement(): boolean {
    const { companyNotification, permissions } = this.props.config;

    const hasNotification =
      permissions &&
      permissions.includes('seeCompanyNotification') &&
      companyNotification &&
      this.props.notification.isActive > 0;
    const userDismissedNotification =
      this.props.notification.isDismissed || sessionStorage.getItem('userDismissedNotification') === 'true';
    return hasNotification && !userDismissedNotification;
  }

  hasBackground(): boolean {
    const { pathname } = this.props.location;
    const urlsWithGreyBackground = ['/dashboard', '/products', '/approvals'];

    return urlsWithGreyBackground.some((url) => pathname.includes(url));
  }

  hasFooter(): boolean {
    const urlsWithoutFooter = [/\/products.*/, /\/accessories\/.+\/.+/, /\/settings.*/];
    return !urlsWithoutFooter.some((url) => Boolean(this.props.location.pathname.match(url)));
  }

  render(): Element {
    const { config, menu, coreActions, children } = this.props;

    const hasGreyBackground = this.hasBackground();
    const showFooter = this.hasFooter();
    const showNotification = this.showNotificationElement();

    const mainComponentClasses = classNames(
      'main',
      showFooter ? 'main--has-footer' : '',
      showNotification ? 'main--has-notification' : '',
    );

    const indexClasses = classNames('index', {
      showMobileNav: menu,
      '-grey-bg': hasGreyBackground,
    });

    return (
      <div className={mainComponentClasses}>
        <Toast
          data={this.props.toast}
          hideToast={coreActions.hideToast}
          onCloseCallback={() => AnalyticsClient.trackEvent(toastEvents.closeToast())}
        />

        <HeaderLayout
          menu={menu}
          config={config}
          cart={this.props.cart}
          link={headerLinks}
          actions={coreActions}
          hasNotification={showNotification}
        />

        <SideNavComponent>{this.getSideNavItems()}</SideNavComponent>

        <div className={indexClasses}>
          <div className="index-content">
            {React.cloneElement(children, {
              coreActions: this.props.coreActions,
              runtimeConfig: this.props.runtimeConfig,
            })}
          </div>
          {showFooter && <Footer selectedStoreCode={this.props.config.selectedStoreCode} />}
        </div>
        {this.displayCurrentOverlay()}
      </div>
    );
  }
}

AppComponent.displayName = 'App';

AppComponent.defaultProps = {
  menu: false,
  overlay: {
    isActive: false,
  },
};

AppComponent.contextTypes = {
  intl: intlShape.isRequired,
};

export default AppComponent;
